import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import DashboardLayout from "./DashboardLayout/DasboardLayout";
import { API_URL } from "./config";

const Drivers = () => {
  const [drivers, setDrivers] = useState([]);
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [driverCount, setDriverCount] = useState(0); // New state for customer count
  useEffect(() => {
    // Fetch data from your endpoint
    fetch(`${API_URL}/driver`)
      .then((response) => response.json())
      .then((data) => {
        setDrivers(data);
        setFilteredDrivers(data);
      })
      .catch((error) => console.error("Error fetching data:", error));
  }, []);

  useEffect(() => {
    let filtered = [...drivers];

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (driver) => driver.membershipstatus === statusFilter
      );
    }

    // Category filter
    if (categoryFilter !== "all") {
      filtered = filtered.filter(
        (driver) => driver.vehicle_category === categoryFilter
      );
    }

    // Date filter
    if (dateFrom && dateTo) {
      filtered = filtered.filter((driver) => {
        const driverDate = new Date(driver.signed_on);
        const fromDate = new Date(dateFrom);
        const toDate = new Date(dateTo);
        return driverDate >= fromDate && driverDate <= toDate;
      });
    }
    setDriverCount(filtered.length); // Update driver count
    setFilteredDrivers(filtered);
  }, [statusFilter, categoryFilter, dateFrom, dateTo, drivers]);

  const statuses = [
    "all",
    ...new Set(drivers.map((driver) => driver.membershipstatus)),
  ];
  const categories = [
    "all",
    ...new Set(drivers.map((driver) => driver.vehicle_category)),
  ].filter(Boolean);

  return (
    <DashboardLayout>
      <div className="p-6 bg-white dark:bg-gray-800 min-h-screen">
        <h1 className="text-3xl font-bold mb-6 text-gray-800 dark:text-white">
          Driver Details
        </h1>

        {/* Driver Count Card */}
        <div className="mb-6 bg-blue-100 border border-blue-300 text-blue-800 text-center p-4 rounded-lg">
          <strong>{driverCount}</strong> Drivers displayed
        </div>

        {/* Filters */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="flex flex-col">
            <label className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              Status
            </label>
            <select
              className="p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {statuses.map((status) => (
                <option key={status} value={status}>
                  {status === "all" ? "All Statuses" : status}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              Vehicle Category
            </label>
            <select
              className="p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category === "all" ? "All Categories" : category}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              Date From
            </label>
            <input
              type="date"
              className="p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>

          <div className="flex flex-col">
            <label className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              Date To
            </label>
            <input
              type="date"
              className="p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto shadow-md rounded-lg">
          <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
            <thead className="text-xs text-gray-700 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-300">
              <tr>
                <th className="px-6 py-3">Driver ID</th>
                <th className="px-6 py-3">Name</th>
                <th className="px-6 py-3">Email</th>
                <th className="px-6 py-3">Phone</th>
                <th className="px-6 py-3">Plate</th>
                <th className="px-6 py-3">Vehicle Category</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Signed On</th>
                <th className="px-6 py-3">Rating</th>
              </tr>
            </thead>
            <tbody>
              {filteredDrivers.map((driver) => (
                <tr
                  key={driver.driver_id}
                  className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  <td className="px-6 py-4">{driver.driver_id}</td>
                  <td className="px-6 py-4">{`${driver.name} ${driver.surname}`}</td>
                  <td className="px-6 py-4">{driver.email || "N/A"}</td>
                  <td className="px-6 py-4">{driver.phone || "N/A"}</td>
                  <td className="px-6 py-4">{driver.plate}</td>
                  <td className="px-6 py-4">
                    {driver.vehicle_category || "N/A"}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${
                        driver.membershipstatus === "Pending Verification"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-green-100 text-green-800"
                      }`}
                    >
                      {driver.membershipstatus}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {driver.signed_on
                      ? format(new Date(driver.signed_on), "MMM dd, yyyy")
                      : "N/A"}
                  </td>
                  <td className="px-6 py-4">{driver.rating || "N/A"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* No results message */}
        {filteredDrivers.length === 0 && (
          <div className="text-center py-4 text-gray-500 dark:text-gray-400">
            No drivers found matching the selected filters.
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Drivers;
